import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { ArrowPathIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import Button from './Button';

interface DeliverySummary {
  delivery_id: string;
  event_type: string;
  resource_type: string;
  resource_path: string;
  channel_type: string;
  channel_id: string;
  status: string;
  attempt_count: number;
  created_at: string;
  delivered_at: string | null;
  last_error: string | null;
}

interface DeliveryListResponse {
  deliveries: DeliverySummary[];
  next_cursor: string | null;
}

const STATUS_BADGE_CLASS: Record<string, string> = {
  delivered: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
  pending: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
  in_progress: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300',
  retrying: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300',
  exhausted: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
};

/**
 * Admin-only "Recent Deliveries" table for the Settings -> Notifications
 * page (AGENTGOV-136). Shows whether webhook/email notifications actually
 * went out, not just that the registry attempted to send them.
 */
const NotificationDeliveryTable: React.FC = () => {
  const [deliveries, setDeliveries] = useState<DeliverySummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState('');

  const fetchDeliveries = useCallback(async (cursor?: string | null, append = false) => {
    setLoading(true);
    setError(null);
    try {
      const params: Record<string, string> = { limit: '25' };
      if (statusFilter) params.status = statusFilter;
      if (cursor) params.cursor = cursor;

      const response = await axios.get<DeliveryListResponse>('/api/notifications/deliveries', {
        params,
      });
      setDeliveries((prev) => (append ? [...prev, ...response.data.deliveries] : response.data.deliveries));
      setNextCursor(response.data.next_cursor);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load notification deliveries');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    fetchDeliveries();
  }, [fetchDeliveries]);

  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h3 className="font-medium text-gray-900 dark:text-white">Recent Deliveries</h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Whether recent notifications (webhook or email) actually went out.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="text-sm px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg
                     bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          >
            <option value="">All statuses</option>
            <option value="delivered">Delivered</option>
            <option value="pending">Pending</option>
            <option value="in_progress">In progress</option>
            <option value="retrying">Retrying</option>
            <option value="exhausted">Exhausted (gave up)</option>
          </select>
          <Button
            variant="ghost"
            size="sm"
            leadingIcon={<ArrowPathIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />}
            onClick={() => fetchDeliveries()}
            disabled={loading}
          >
            Refresh
          </Button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 text-sm text-red-800 dark:text-red-200 flex items-center gap-2">
          <InformationCircleIcon className="h-5 w-5 flex-shrink-0" />
          {error}
        </div>
      )}

      {!error && deliveries.length === 0 && !loading && (
        <p className="text-sm text-gray-500 dark:text-gray-400 py-6 text-center">
          No notification deliveries yet.
        </p>
      )}

      {deliveries.length > 0 && (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase border-b border-gray-200 dark:border-gray-700">
                <th className="py-2 pr-4">When</th>
                <th className="py-2 pr-4">Event</th>
                <th className="py-2 pr-4">Resource</th>
                <th className="py-2 pr-4">Channel</th>
                <th className="py-2 pr-4">Status</th>
                <th className="py-2 pr-4">Attempts</th>
                <th className="py-2">Last error</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
              {deliveries.map((d) => (
                <tr key={d.delivery_id} className="text-gray-800 dark:text-gray-200">
                  <td className="py-2 pr-4 whitespace-nowrap">
                    {new Date(d.created_at).toLocaleString()}
                  </td>
                  <td className="py-2 pr-4 whitespace-nowrap">{d.event_type}</td>
                  <td className="py-2 pr-4 font-mono text-xs">{d.resource_path}</td>
                  <td className="py-2 pr-4 whitespace-nowrap">{d.channel_type}/{d.channel_id}</td>
                  <td className="py-2 pr-4">
                    <span
                      className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        STATUS_BADGE_CLASS[d.status] || 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                      }`}
                    >
                      {d.status}
                    </span>
                  </td>
                  <td className="py-2 pr-4">{d.attempt_count}</td>
                  <td className="py-2 max-w-xs truncate text-xs text-red-700 dark:text-red-400" title={d.last_error || ''}>
                    {d.last_error || ''}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {nextCursor && (
        <div className="flex justify-center">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => fetchDeliveries(nextCursor, true)}
            disabled={loading}
          >
            Load more
          </Button>
        </div>
      )}
    </div>
  );
};

export default NotificationDeliveryTable;
